import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import posthog from "posthog-js";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { DefaultCatchBoundary } from "@/components/default-catch-boundary";
import { NotFound } from "@/components/not-found";
import { TooltipProvider } from "@/components/ui/tooltip";
import { usePostHogPageview } from "@/hooks/use-posthog-pageview";
import appCss from "@/styles.css?url";
import { canonicalLink, seo } from "@/utils/seo";
// @ts-expect-error Font package has no type declarations
import "@fontsource-variable/inter";
// @ts-expect-error Font package has no type declarations
import "@fontsource-variable/jetbrains-mono";
// @ts-expect-error Font package has no type declarations
import "@fontsource/bebas-neue";

function PostHogInit() {
  useEffect(() => {
    const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
    const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
    if (key && typeof window !== "undefined") {
      posthog.init(key, {
        api_host: host || "https://eu.i.posthog.com",
        capture_pageview: false,
        capture_pageleave: true,
      });
    }
  }, []);
  return null;
}

function RootDocument({ children }: { children: React.ReactNode }) {
  usePostHogPageview();

  return (
    <html lang="en" className="dark">
      <head>
        <HeadContent />
      </head>
      <body className="min-h-screen bg-background text-foreground antialiased">
        <noscript>
          <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8" }}>
            <p>
              This application requires JavaScript to be enabled. Please enable JavaScript in your
              browser settings.
            </p>
          </div>
        </noscript>
        <TooltipProvider delayDuration={300}>{children}</TooltipProvider>
        <PostHogInit />

        <Toaster richColors position="bottom-right" theme="dark" />
        <Scripts />
      </body>
    </html>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1" },
      ...seo({
        title: "Setups by SZESE — iRacing Setup Shop",
        description:
          "Professional iRacing car setups by SZESE E-Sport. Download race-ready setups crafted by world-class sim racers.",
        keywords:
          "iRacing, sim racing, car setups, SZESE, e-sport, sim racing setups, iRacing setups",
        image: "https://setupsbyszese.com/og-image.png",
        canonical: "https://setupsbyszese.com",
      }),
    ],
    links: [
      canonicalLink("https://setupsbyszese.com"),
      // Bebas Neue now self-hosted via @fontsource/bebas-neue
      // DNS prefetch for external APIs
      { rel: "dns-prefetch", href: "https://eu.i.posthog.com" },
      { rel: "dns-prefetch", href: "https://api.polar.sh" },
      { rel: "stylesheet", href: appCss },
      // Dark mode: transparent favicon (white text visible on dark tabs)
      {
        rel: "icon",
        type: "image/x-icon",
        href: "/favicon.ico",
        media: "(prefers-color-scheme: dark)",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
        media: "(prefers-color-scheme: dark)",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
        media: "(prefers-color-scheme: dark)",
      },
      // Light mode: dark background favicon (visible on light tabs)
      {
        rel: "icon",
        type: "image/x-icon",
        href: "/favicon-light.ico",
        media: "(prefers-color-scheme: light)",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-light-32x32.png",
        media: "(prefers-color-scheme: light)",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-light-16x16.png",
        media: "(prefers-color-scheme: light)",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
    ],
  }),
  errorComponent: (props) => (
    <RootDocument>
      <DefaultCatchBoundary {...props} />
    </RootDocument>
  ),
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
      {import.meta.env.DEV && (
        <>
          <TanStackRouterDevtools position="bottom-right" />
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </>
      )}
    </RootDocument>
  );
}
